/**
 * Generic pie / sliced-circle canvas icon generator.
 *
 * Used by:
 *   - generateWeekDayIcon  (day-colour slices)
 *   - generateRouteIcon    (route-colour slices)
 *   - PieClusterMarker layer (global cluster pie)
 *
 * The result is a plain HTMLCanvasElement. Call .toDataURL() to get a
 * data URL for IconLayer, or pass the canvas directly to createImageBitmap().
 */
import { hexToRgb } from './colorUtils'

function isLabelLight(hex) {
  const c = (hex || '').replace('#', '')
  if (c.length < 6) return false
  const r = parseInt(c.substring(0, 2), 16) / 255
  const g = parseInt(c.substring(2, 4), 16) / 255
  const b = parseInt(c.substring(4, 6), 16) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.35
}

/**
 * @param {string[]} colors          - Slice fill colours in order. One colour = full circle.
 * @param {object}   opts
 * @param {number}   opts.size        - Diameter in CSS px (default 40).
 * @param {number}   opts.borderWidth - Ring width between outer edge and slices (default 2).
 * @param {string}   opts.borderColor - Ring/background fill colour (default '#FFFFFF').
 * @param {string}   [opts.label]     - Optional count label drawn in the centre.
 * @param {string}   [opts.labelColor] - Label text colour (default '#FFFFFF').
 * @returns {HTMLCanvasElement}
 */
const OVERSAMPLE = 3

export function generatePieIcon(
  colors,
  { size = 40, borderWidth = 3, borderColor = '#FFFFFF', label, labelColor = '#FFFFFF', outlineColor = null, outlineWidth = 2 } = {}
) {
  const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE
  // Halo grows the canvas by 2*outlineWidth on each axis so the ring sits
  // OUTSIDE the existing pie — leaves the inner artwork pixel-exact.
  const haloW = outlineColor ? outlineWidth : 0
  const totalSize = size + 2 * haloW
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(totalSize * dpi)
  canvas.height = Math.ceil(totalSize * dpi)

  const ctx = canvas.getContext('2d')
  ctx.scale(dpi, dpi)

  const cx = totalSize / 2
  const cy = totalSize / 2
  const outerR = size / 2
  const innerR = outerR - borderWidth

  if (!colors || colors.length === 0) return canvas

  // Outer halo ring — drawn first so the rest of the icon stacks on top
  if (haloW > 0) {
    ctx.beginPath()
    ctx.arc(cx, cy, outerR + haloW, 0, 2 * Math.PI)
    ctx.fillStyle = outlineColor
    ctx.fill()
  }

  // Border ring
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, outerR, 0, 2 * Math.PI)
  ctx.clip()
  ctx.fillStyle = borderColor
  ctx.fillRect(0, 0, totalSize, totalSize)
  ctx.restore()

  if (colors.length === 1) {
    ctx.beginPath()
    ctx.arc(cx, cy, innerR, 0, 2 * Math.PI)
    ;[ctx.fillStyle] = colors
    ctx.fill()
  } else {
    // Multiple equal-size slices
    const sliceAngle = (2 * Math.PI) / colors.length
    for (let i = 0; i < colors.length; i++) {
      const startAngle = i * sliceAngle - Math.PI / 2 // start at 12 o'clock
      const endAngle = (i + 1) * sliceAngle - Math.PI / 2
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, innerR, startAngle, endAngle)
      ctx.closePath()
      ctx.fillStyle = colors[i]
      ctx.fill()
    }
  }

  // Optional centred count label — drawn at native DPR so it's as sharp as DOM text
  if (label != null) {
    const fontSize = Math.max(14, Math.round(size * 0.38))
    ctx.font = `bold ${fontSize}px "Inter", sans-serif`
    ctx.fillStyle = labelColor
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    // Only add shadow when label needs it against the pie background.
    // For single-color pies, check if label is light on dark; skip on light backgrounds.
    const bgHex = colors.length === 1 ? colors[0] : null
    const isLightLabel = bgHex ? isLabelLight(labelColor) : true
    const isDarkBg = bgHex ? !isLabelLight(bgHex) : true
    if (isLightLabel && isDarkBg) {
      ctx.shadowColor = 'rgba(0,0,0,0.85)'
      ctx.shadowBlur = 4
      ctx.shadowOffsetX = 0
      ctx.shadowOffsetY = 2
      // Draw twice — second pass deepens the shadow for better contrast.
      ctx.fillText(String(label), cx, cy)
    }
    ctx.fillText(String(label), cx, cy)
    ctx.shadowColor = 'transparent'
  }

  // Anti-dark-fringe fix: Canvas 2D stores pixels in premultiplied alpha, so fully
  // transparent pixels (alpha=0) always have RGB=0 regardless of what was drawn.
  // When deck.gl's GL bilinear filter samples the icon edge in the texture atlas,
  // it interpolates between the semi-transparent colored edge pixels and the
  // transparent-black background, producing a dark fringe.
  //
  // Fix: set all alpha=0 pixels to alpha=1 with the border color's RGB.
  // alpha=1 (1/255 ≈ 0.4% opacity) is visually imperceptible but preserves the
  // RGB through premultiplied storage and through drawImage onto the atlas, so GL
  // bilinear filtering interpolates toward the border color rather than black.
  const [br, bg, bb] = hexToRgb(borderColor)
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const px = imgData.data
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] === 0) {
      px[i] = br
      px[i + 1] = bg
      px[i + 2] = bb
      px[i + 3] = 1
    }
  }
  ctx.putImageData(imgData, 0, 0)

  return canvas
}

/** Convenience: returns a data URL directly. */
export function generatePieIconUrl(colors, opts) {
  return generatePieIcon(colors, opts).toDataURL()
}
